import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import { Plus, X, ChevronLeft, ChevronRight } from 'lucide-react'

const PIATTAFORME = [
  { id: 'instagram', label: 'Instagram', color: '#E1306C' },
  { id: 'facebook',  label: 'Facebook',  color: '#1877F2' },
  { id: 'linkedin',  label: 'LinkedIn',  color: '#0A66C2' },
  { id: 'tiktok',    label: 'TikTok',    color: '#010101' },
  { id: 'youtube',   label: 'YouTube',   color: '#FF0000' },
  { id: 'altro',     label: 'Altro',     color: '#6B7280' },
]

const STATUS = [
  { id: 'bozza',     label: 'Bozza',      bg: 'bg-gray-700',    text: 'text-gray-300' },
  { id: 'in_review', label: 'In Review',  bg: 'bg-amber-500/20', text: 'text-amber-400' },
  { id: 'approvato', label: 'Approvato',  bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  { id: 'pubblicato',label: 'Pubblicato', bg: 'bg-violet-500/20', text: 'text-violet-400' },
  { id: 'archiviato',label: 'Archiviato', bg: 'bg-gray-800',    text: 'text-gray-500' },
]

const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre']
const GIORNI = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom']

const piattaforma = (id) => PIATTAFORME.find(p => p.id === id) || PIATTAFORME[5]
const statusCfg = (id) => STATUS.find(s => s.id === id) || STATUS[0]
const dowLun = (d) => (d.getDay() + 6) % 7
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`

// ─── PostModal ─────────────────────────────────────────────────────────────────
const EMPTY_POST = { piattaforma: 'instagram', data_pubblicazione: '', ora_pubblicazione: '', caption: '', hashtags: '', note: '', status: 'bozza', project_id: '', assegnato_a: '' }

const PostModal = ({ post, progetti, teamMembers, onClose, onSaved, onDeleted, defaultDate }) => {
  const { profile } = useAuth()
  const isEdit = !!post?.id
  const [form, setForm] = useState(isEdit ? { ...post, assegnato_a: post.assegnato_a || '', project_id: post.project_id || '' } : { ...EMPTY_POST, data_pubblicazione: defaultDate || '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.piattaforma) return
    setLoading(true)
    setError(null)
    const payload = {
      piattaforma: form.piattaforma,
      data_pubblicazione: form.data_pubblicazione || null,
      ora_pubblicazione: form.ora_pubblicazione || null,
      caption: form.caption?.trim() || null,
      hashtags: form.hashtags?.trim() || null,
      note: form.note?.trim() || null,
      status: form.status,
      project_id: form.project_id || null,
      assegnato_a: form.assegnato_a || null,
    }
    let data, err
    if (isEdit) {
      ;({ data, error: err } = await supabase.from('social_posts').update(payload).eq('id', post.id).select('*, progetto:project_id(titolo,colore), assegnato:assegnato_a(full_name)').single())
    } else {
      ;({ data, error: err } = await supabase.from('social_posts').insert({ ...payload, created_by: profile.id }).select('*, progetto:project_id(titolo,colore), assegnato:assegnato_a(full_name)').single())
    }
    if (err) { setError(err.message); setLoading(false); return }
    onSaved(data)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    await supabase.from('social_posts').delete().eq('id', post.id)
    onDeleted(post.id)
    onClose()
  }

  const inputCls = "w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors"
  const labelCls = "block text-sm text-gray-400 mb-1"

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Modifica post' : 'Nuovo post'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Piattaforma */}
          <div>
            <label className={labelCls}>Piattaforma *</label>
            <div className="flex flex-wrap gap-2">
              {PIATTAFORME.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, piattaforma: p.id }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    form.piattaforma === p.id
                      ? 'border-transparent text-white'
                      : 'border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                  style={form.piattaforma === p.id ? { backgroundColor: p.color + '33', borderColor: p.color, color: p.color } : {}}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Data e ora */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Data pubblicazione</label>
              <input type="date" value={form.data_pubblicazione} onChange={set('data_pubblicazione')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Ora</label>
              <input type="time" value={form.ora_pubblicazione} onChange={set('ora_pubblicazione')} className={inputCls} />
            </div>
          </div>

          {/* Caption */}
          <div>
            <label className={labelCls}>Caption</label>
            <textarea value={form.caption} onChange={set('caption')} rows={4} placeholder="Testo del post..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors resize-none" />
          </div>

          {/* Hashtags */}
          <div>
            <label className={labelCls}>Hashtag</label>
            <input value={form.hashtags} onChange={set('hashtags')} placeholder="#hashtag #altro" className={inputCls} />
          </div>

          {/* Note */}
          <div>
            <label className={labelCls}>Note interne</label>
            <textarea value={form.note} onChange={set('note')} rows={2} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors resize-none" />
          </div>

          {/* Status + Progetto + Assegnato */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Status</label>
              <select value={form.status} onChange={set('status')} className={inputCls}>
                {STATUS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Progetto</label>
              <select value={form.project_id} onChange={set('project_id')} className={inputCls}>
                <option value="">— Nessuno —</option>
                {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Assegnato a</label>
            <select value={form.assegnato_a} onChange={set('assegnato_a')} className={inputCls}>
              <option value="">— Nessuno —</option>
              {teamMembers.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
            </select>
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-3 pt-2">
            {isEdit && (
              <button type="button" onClick={handleDelete}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  confirmDelete
                    ? 'bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white'
                    : 'border border-gray-700 text-gray-500 hover:text-red-400 hover:border-red-500/40'
                }`}>
                {confirmDelete ? 'Conferma' : 'Elimina'}
              </button>
            )}
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm">
              Annulla
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors">
              {loading ? 'Salvataggio...' : isEdit ? 'Salva' : 'Aggiungi'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Social ────────────────────────────────────────────────────────────────────
const Social = () => {
  const now = new Date()
  const [anno, setAnno] = useState(now.getFullYear())
  const [mese, setMese] = useState(now.getMonth())
  const [posts, setPosts] = useState([])
  const [progetti, setProgetti] = useState([])
  const [teamMembers, setTeamMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [selectedDay, setSelectedDay] = useState(null)
  const [filterPiattaforma, setFilterPiattaforma] = useState('all')

  const fetchPosts = useCallback(async () => {
    const inizio = `${anno}-${String(mese+1).padStart(2,'0')}-01`
    const fine   = new Date(anno, mese+1, 0)
    const fineStr = isoDate(fine)
    const { data } = await supabase
      .from('social_posts')
      .select('*, progetto:project_id(titolo,colore), assegnato:assegnato_a(full_name)')
      .gte('data_pubblicazione', inizio)
      .lte('data_pubblicazione', fineStr)
      .order('data_pubblicazione')
      .order('ora_pubblicazione')
    setPosts(data || [])
    setLoading(false)
  }, [anno, mese])

  useEffect(() => {
    Promise.all([
      supabase.from('progetti').select('id,titolo,colore').order('titolo'),
      supabase.from('profiles').select('id,full_name').order('full_name'),
    ]).then(([{ data: pr }, { data: tm }]) => {
      setProgetti(pr || [])
      setTeamMembers(tm || [])
    })
  }, [])

  useEffect(() => {
    setLoading(true)
    fetchPosts()
    const channel = supabase
      .channel('social-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'social_posts' }, fetchPosts)
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [fetchPosts])

  const prevMese = () => { if (mese === 0) { setAnno(a => a-1); setMese(11) } else setMese(m => m-1); setSelectedDay(null) }
  const nextMese = () => { if (mese === 11) { setAnno(a => a+1); setMese(0) } else setMese(m => m+1); setSelectedDay(null) }

  const handleSaved = (saved) => {
    setPosts(prev => {
      const exists = prev.find(p => p.id === saved.id)
      return exists ? prev.map(p => p.id === saved.id ? saved : p) : [...prev, saved]
    })
  }
  const handleDeleted = (id) => setPosts(prev => prev.filter(p => p.id !== id))

  // Calendario grid
  const primo = new Date(anno, mese, 1)
  const ultimo = new Date(anno, mese+1, 0)
  const offset = dowLun(primo)
  const totaleCelle = Math.ceil((offset + ultimo.getDate()) / 7) * 7
  const celle = Array.from({ length: totaleCelle }, (_, i) => {
    const g = i - offset + 1
    return (g < 1 || g > ultimo.getDate()) ? null : g
  })

  const filteredPosts = posts.filter(p => filterPiattaforma === 'all' || p.piattaforma === filterPiattaforma)

  const postsPerGiorno = {}
  filteredPosts.forEach(p => {
    if (p.data_pubblicazione) {
      if (!postsPerGiorno[p.data_pubblicazione]) postsPerGiorno[p.data_pubblicazione] = []
      postsPerGiorno[p.data_pubblicazione].push(p)
    }
  })

  const todayStr = isoDate(now)
  const selectedStr = selectedDay
    ? `${anno}-${String(mese+1).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}`
    : null
  const postsSelected = selectedStr ? (postsPerGiorno[selectedStr] || []) : []

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Social</h1>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <button onClick={prevMese} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"><ChevronLeft size={18} /></button>
            <span className="text-white font-semibold min-w-36 text-center">{MESI[mese]} {anno}</span>
            <button onClick={nextMese} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"><ChevronRight size={18} /></button>
          </div>
          <button
            onClick={() => setModal({ new: true })}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm"
          >
            <Plus size={15} />
            Nuovo post
          </button>
        </div>
      </div>

      {/* Piattaforma filter pills */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          onClick={() => setFilterPiattaforma('all')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${filterPiattaforma === 'all' ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-800 text-gray-500 hover:border-gray-700'}`}
        >
          Tutte ({posts.length})
        </button>
        {PIATTAFORME.map(p => {
          const count = posts.filter(post => post.piattaforma === p.id).length
          if (!count && filterPiattaforma !== p.id) return null
          return (
            <button
              key={p.id}
              onClick={() => setFilterPiattaforma(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors`}
              style={filterPiattaforma === p.id
                ? { backgroundColor: p.color + '22', borderColor: p.color, color: p.color }
                : { borderColor: '#374151', color: '#9CA3AF' }}
            >
              {p.label} {count > 0 && `(${count})`}
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Calendario */}
        <div className="xl:col-span-2 bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-7 border-b border-gray-800">
            {GIORNI.map(g => (
              <div key={g} className="py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{g}</div>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-7">
              {celle.map((giorno, idx) => {
                if (!giorno) return <div key={idx} className="min-h-24 border-b border-r border-gray-800/50" />
                const dateStr = `${anno}-${String(mese+1).padStart(2,'0')}-${String(giorno).padStart(2,'0')}`
                const isToday = dateStr === todayStr
                const isSelected = dateStr === selectedStr
                const dayPosts = postsPerGiorno[dateStr] || []

                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedDay(giorno === selectedDay ? null : giorno)}
                    className={`min-h-24 border-b border-r border-gray-800/50 p-1.5 cursor-pointer transition-colors ${isSelected ? 'bg-violet-600/10' : 'hover:bg-gray-800/40'}`}
                  >
                    <span className={`text-sm font-medium inline-flex w-7 h-7 items-center justify-center rounded-full mb-1 ${
                      isToday ? 'bg-violet-600 text-white' : isSelected ? 'text-violet-400' : 'text-gray-400'
                    }`}>{giorno}</span>

                    {dayPosts.slice(0, 3).map((p, i) => {
                      const plt = piattaforma(p.piattaforma)
                      return (
                        <div
                          key={i}
                          onClick={e => { e.stopPropagation(); setModal(p) }}
                          className="w-full text-left rounded px-1 py-0.5 mb-0.5 truncate text-xs leading-tight"
                          style={{ backgroundColor: plt.color + '22', color: plt.color }}
                        >
                          {p.ora_pubblicazione ? p.ora_pubblicazione.slice(0,5) + ' · ' : ''}{plt.label}
                        </div>
                      )
                    })}
                    {dayPosts.length > 3 && (
                      <p className="text-xs text-gray-600 px-1">+{dayPosts.length - 3}</p>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Pannello laterale */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-gray-800">
            <h2 className="text-sm font-semibold text-white">
              {selectedDay ? `${selectedDay} ${MESI[mese]}` : 'Seleziona un giorno'}
            </h2>
          </div>

          <div className="flex-1 p-3 overflow-y-auto">
            {!selectedDay && (
              <p className="text-gray-600 text-sm text-center py-8">Clicca su un giorno per vedere i post</p>
            )}
            {selectedDay && postsSelected.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-600 text-sm mb-3">Nessun post in questo giorno</p>
                <button
                  onClick={() => setModal({ new: true })}
                  className="text-violet-400 hover:text-violet-300 text-xs transition-colors"
                >
                  + Aggiungi post →
                </button>
              </div>
            )}
            {postsSelected.map((p) => {
              const plt = piattaforma(p.piattaforma)
              const st = statusCfg(p.status)
              return (
                <div
                  key={p.id}
                  onClick={() => setModal(p)}
                  className="rounded-xl p-3 mb-2 cursor-pointer hover:bg-gray-800/60 transition-colors border border-gray-800"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium" style={{ color: plt.color }}>{plt.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
                  </div>
                  {p.caption && <p className="text-white text-xs line-clamp-2 mb-1">{p.caption}</p>}
                  {p.ora_pubblicazione && <p className="text-gray-500 text-xs">{p.ora_pubblicazione.slice(0,5)}</p>}
                  {p.assegnato && <p className="text-gray-600 text-xs mt-1">{p.assegnato.full_name}</p>}
                </div>
              )
            })}
          </div>

          {selectedDay && (
            <div className="px-4 py-3 border-t border-gray-800">
              <button
                onClick={() => setModal({ new: true, defaultDate: selectedStr })}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed border-gray-700 text-gray-500 hover:text-violet-400 hover:border-violet-500/40 transition-colors text-sm"
              >
                <Plus size={14} /> Post per questo giorno
              </button>
            </div>
          )}

          {/* Stats mese */}
          <div className="px-5 py-4 border-t border-gray-800">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Questo mese</p>
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Post totali</span>
                <span className="text-white font-medium">{posts.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Pubblicati</span>
                <span className="text-violet-400 font-medium">{posts.filter(p => p.status === 'pubblicato').length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Da approvare</span>
                <span className="text-amber-400 font-medium">{posts.filter(p => p.status === 'in_review').length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {modal && (
        <PostModal
          post={modal?.new ? null : modal}
          progetti={progetti}
          teamMembers={teamMembers}
          defaultDate={modal?.defaultDate || (selectedStr || '')}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}

export default Social
